// Centralised data sources for case studies and insights.
// Used by Works carousel, /works/:slug detail pages, and Insights/blog.

export const CASES = [
  {
    slug: "nusapost-news-platform",
    title: {
      id: "Platform Berita Multi-Region untuk Jaringan Media Nasional",
      en: "Multi-Region News Platform for a National Media Network",
    },
    client: "NusaPost Network",
    industry: { id: "Media & Penerbitan", en: "Media & Publishing" },
    year: "2024",
    scope: ["UI/UX Research", "Web + App Development", "Cyber Security"],
    bg: "linear-gradient(135deg, #1a1a26 0%, #4f3fcc 65%, #5b4bff 100%)",
    accent: "#d8ff5c",
    accentText: "#0c0e14",
    initials: "NP",
    metric: { id: "+220% lalu lintas organik", en: "+220% organic traffic" },
    summary: {
      id: "Sistem editorial terpusat dengan distribusi konten lintas 14 kanal regional dan pencarian semantik berbasis vektor.",
      en: "A centralised editorial system distributing content across 14 regional channels with vector-based semantic search.",
    },
    challenge: {
      id: "14 redaksi daerah bekerja dengan CMS berbeda — alur publikasi tidak konsisten, SEO tidak terstandar, dan pencarian situs berkinerja buruk untuk konten yang melebihi 2 juta artikel.",
      en: "14 regional newsrooms operated on different CMSes — publishing flows were inconsistent, SEO was non-standard, and on-site search underperformed across 2M+ archived articles.",
    },
    approach: {
      id: "Kami membangun ulang CMS sebagai monorepo Next.js + Node API yang shared, dengan plugin SEO otomatis, queue penerbitan multi-channel, dan indeks vektor pgvector untuk pencarian semantik berbahasa Indonesia.",
      en: "We rebuilt the CMS as a shared Next.js + Node API monorepo, with automated SEO plugins, a multi-channel publishing queue, and a pgvector index for Bahasa-Indonesia semantic search.",
    },
    outcome: {
      id: "Dalam 6 bulan, organic traffic naik 220%, waktu publikasi rata-rata turun dari 23 menit menjadi 4 menit, dan kepuasan redaksi meningkat menjadi 4.7/5.",
      en: "In 6 months, organic traffic grew 220%, average time-to-publish dropped from 23 to 4 minutes, and editor satisfaction rose to 4.7/5.",
    },
  },
  {
    slug: "karya-mandiri-erp",
    title: {
      id: "Sistem ERP Terintegrasi untuk Manufaktur Skala Menengah",
      en: "Integrated ERP for a Mid-Sized Manufacturer",
    },
    client: "Karya Mandiri Group",
    industry: { id: "Manufaktur", en: "Manufacturing" },
    year: "2024",
    scope: ["Web + App Development", "Customer Experience", "IoT"],
    bg: "linear-gradient(135deg, #161821 0%, #232842 60%, #3a2f7d 100%)",
    accent: "#8174ff",
    accentText: "#ffffff",
    initials: "KM",
    metric: { id: "-38% waktu siklus produksi", en: "-38% production cycle time" },
    summary: {
      id: "Modul keuangan, gudang, dan produksi yang terhubung dengan sensor IoT di tiga pabrik, dashboard eksekutif real-time.",
      en: "Finance, warehouse, and production modules wired to IoT sensors across three factories with a real-time executive dashboard.",
    },
    challenge: {
      id: "Tiga pabrik di Cikarang, Pasuruan, dan Medan beroperasi dengan spreadsheet dan ERP legacy berbeda. Direksi tidak memiliki visibilitas konsolidasi yang akurat lebih dari mingguan.",
      en: "Three plants in Cikarang, Pasuruan, and Medan ran on disparate spreadsheets and legacy ERPs. Leadership lacked accurate consolidated visibility beyond weekly intervals.",
    },
    approach: {
      id: "Kami merancang ulang proses inti, mengintegrasikan sensor IoT pada lini produksi, dan membangun ERP modular berbasis NestJS + PostgreSQL dengan dashboard real-time untuk direksi.",
      en: "We redesigned core processes, integrated IoT sensors on production lines, and built a modular NestJS + PostgreSQL ERP with a real-time executive dashboard.",
    },
    outcome: {
      id: "Waktu siklus produksi turun 38%, akurasi stok mencapai 99.4%, dan rapat direksi mingguan kini dapat mengakses metrik real-time, bukan laporan tertunda 5 hari.",
      en: "Cycle time dropped 38%, stock accuracy reached 99.4%, and weekly leadership meetings now run on real-time metrics rather than 5-day-old reports.",
    },
  },
  {
    slug: "terralogis-bos",
    title: {
      id: "Business Operational System untuk Operator Logistik",
      en: "Business Operational System for a Logistics Operator",
    },
    client: "TerraLogis Indonesia",
    industry: { id: "Logistik & Rantai Pasok", en: "Logistics & Supply Chain" },
    year: "2023",
    scope: ["Digital Marketing", "Web + App Development", "Cyber Security"],
    bg: "linear-gradient(135deg, #0a1f14 0%, #14361f 55%, #1f5733 100%)",
    accent: "#00f5ff",
    accentText: "#0a1f14",
    initials: "TL",
    metric: { id: "+150% efisiensi armada", en: "+150% fleet efficiency" },
    summary: {
      id: "Pelacakan armada multi-modal, rekonsiliasi tagihan otomatis, dan portal pelanggan dengan SLA terukur.",
      en: "Multi-modal fleet tracking, automated billing reconciliation, and a customer portal with measurable SLAs.",
    },
    challenge: {
      id: "Operasi 400+ truk dan 12 gudang transit dijalankan dengan kombinasi WhatsApp, Excel, dan sistem pihak ketiga yang tidak saling bertukar data — rekonsiliasi tagihan butuh 14 hari kerja.",
      en: "Operations across 400+ trucks and 12 transit hubs ran on a mix of WhatsApp, Excel, and disconnected third-party systems — billing reconciliation took 14 working days.",
    },
    approach: {
      id: "Kami mendesain BOS sebagai single-source-of-truth: telematika real-time, mesin perutean, modul tagihan, dan portal klien dengan SLA otomatis dan notifikasi via WhatsApp Business API.",
      en: "We designed the BOS as a single source of truth: real-time telematics, a routing engine, billing module, and client portal with automated SLAs and WhatsApp Business API alerts.",
    },
    outcome: {
      id: "Utilisasi armada naik 150%, rekonsiliasi tagihan turun ke 36 jam, dan TerraLogis memenangkan 3 kontrak enterprise baru karena SLA dapat dibuktikan secara terukur.",
      en: "Fleet utilisation rose 150%, billing reconciliation dropped to 36 hours, and TerraLogis won three new enterprise contracts on the back of provable SLAs.",
    },
  },
  {
    slug: "kopi-suara-pos",
    title: {
      id: "Point of Sales Modern untuk Jaringan F&B Premium",
      en: "Modern POS for a Premium F&B Chain",
    },
    client: "Kopi Suara",
    industry: { id: "Makanan & Minuman", en: "Food & Beverage" },
    year: "2024",
    scope: ["Logo + Branding", "UI/UX Research", "Customer Experience"],
    bg: "linear-gradient(135deg, #14171f 0%, #1f1a3c 55%, #5b4bff 130%)",
    accent: "#ff5722",
    accentText: "#ffffff",
    initials: "KS",
    metric: { id: "+3.2× kecepatan transaksi", en: "+3.2× transaction speed" },
    summary: {
      id: "POS offline-first dengan integrasi inventori, loyalitas berbasis token, dan dashboard performa per outlet.",
      en: "An offline-first POS with inventory integration, token-based loyalty, and per-outlet performance dashboards.",
    },
    challenge: {
      id: "POS lama berbasis Windows lambat di jam sibuk, sering offline tanpa fallback, dan tidak mendukung loyalty program yang sedang ekspansi ke 24 outlet baru.",
      en: "The legacy Windows POS slowed during peaks, frequently went offline without fallback, and could not support the loyalty program planned for 24 new outlets.",
    },
    approach: {
      id: "Kami merancang POS web offline-first berbasis React + IndexedDB dengan sinkronisasi cerdas, mengintegrasikan inventori, dan membangun loyalty token yang berfungsi tanpa koneksi.",
      en: "We built an offline-first React + IndexedDB POS with smart sync, integrated inventory, and a loyalty token engine that works without connectivity.",
    },
    outcome: {
      id: "Kecepatan transaksi meningkat 3.2 kali, kegagalan transaksi karena offline turun ke <0.05%, dan program loyalty mencatat 38% retensi pengguna bulanan.",
      en: "Transaction speed improved 3.2×, offline-related failures dropped to <0.05%, and the loyalty program now sees 38% monthly user retention.",
    },
  },
];

export const INSIGHTS = [
  {
    slug: "kerangka-transformasi-digital-umkm",
    title: {
      id: "Kerangka Pragmatis Transformasi Digital untuk UMKM Indonesia",
      en: "A Pragmatic Digital Transformation Framework for Indonesian SMEs",
    },
    excerpt: {
      id: "Lima langkah yang dapat dijalankan UMKM tanpa anggaran enterprise — fokus pada kemenangan kecil yang membentuk momentum nyata.",
      en: "Five steps any SME can execute without an enterprise budget — focused on small wins that compound into real momentum.",
    },
    category: { id: "Strategi", en: "Strategy" },
    readingTime: 8,
    date: "2025-11-12",
    cover: "linear-gradient(135deg, #5b4bff 0%, #1a1a26 100%)",
    body: {
      id: [
        "Banyak UMKM Indonesia merasa transformasi digital adalah proyek besar yang hanya terjangkau oleh perusahaan tier-1. Pengalaman kami menunjukkan sebaliknya: kemenangan kecil yang konsisten jauh lebih kuat daripada satu inisiatif besar yang setengah jalan.",
        "Langkah 1 — Pilih satu proses yang paling menyakitkan. Bukan yang paling besar, bukan yang paling 'keren'. Yang paling menyakitkan minggu lalu.",
        "Langkah 2 — Petakan ulang proses sebelum menulis kode satu baris pun. 70% kegagalan transformasi yang kami lihat berasal dari otomatisasi proses yang sebenarnya tidak layak.",
        "Langkah 3 — Bangun versi paling sederhana yang berfungsi. Anda akan lebih banyak belajar dari implementasi dua minggu daripada dari studi tiga bulan.",
        "Langkah 4 — Ukur sebelum dan sesudah. Tanpa baseline, Anda tidak punya argumen untuk meneruskan momentum.",
        "Langkah 5 — Rayakan kemenangan kecil dan lanjutkan. Pertumbuhan digital adalah seri sprint, bukan satu maraton.",
      ],
      en: [
        "Many Indonesian SMEs feel digital transformation is a large project only top-tier corporations can afford. Our experience says otherwise: small consistent wins are far more powerful than a single grand initiative that stalls halfway.",
        "Step 1 — Pick the most painful process. Not the biggest. Not the trendiest. The one that hurt the most last week.",
        "Step 2 — Re-map the process before writing a single line of code. 70% of the failed transformations we've seen automated processes that were not fit to begin with.",
        "Step 3 — Ship the simplest possible version that works. You will learn more from two weeks of implementation than from three months of study.",
        "Step 4 — Measure before and after. Without a baseline, you have no argument to sustain momentum.",
        "Step 5 — Celebrate small wins and continue. Digital growth is a series of sprints, not one marathon.",
      ],
    },
  },
  {
    slug: "memilih-stack-teknologi-yang-tahan-lama",
    title: {
      id: "Memilih Stack Teknologi yang Tahan Lama, Bukan yang Sedang Tren",
      en: "Choosing a Durable Tech Stack — Not Whatever Is Trending",
    },
    excerpt: {
      id: "Bagaimana cara mengevaluasi teknologi baru tanpa terjebak hype cycle yang merugikan tim Anda dalam 18 bulan.",
      en: "How to evaluate new technology without getting trapped in a hype cycle that will hurt your team 18 months from now.",
    },
    category: { id: "Engineering", en: "Engineering" },
    readingTime: 6,
    date: "2025-10-28",
    cover: "linear-gradient(135deg, #14171f 0%, #d8ff5c 220%)",
    body: {
      id: [
        "Setiap minggu ada framework, database, atau platform baru yang diklaim akan menggantikan segalanya. Bagaimana memilih tanpa terjebak?",
        "Pertanyaan pertama: apakah teknologi ini menyelesaikan masalah konkret yang kami punya? Bukan masalah yang Twitter katakan kami punya.",
        "Pertanyaan kedua: berapa biaya ketergantungannya jika vendor menghilang? Setiap teknologi memiliki exit cost. Hitung sebelum masuk.",
        "Pertanyaan ketiga: siapa yang akan memeliharanya 24 bulan dari sekarang? Jika jawabannya 'tidak ada yang tahu', kembali ke pilihan yang lebih membosankan tapi terbukti.",
        "Stack kami tahun ini mungkin terlihat membosankan: PostgreSQL, FastAPI, React, Postgres+pgvector untuk AI. Itu disengaja. Kami memilih teknologi yang dapat kami pertanggungjawabkan kepada klien dalam 5 tahun ke depan.",
      ],
      en: [
        "Every week there's a new framework, database, or platform claiming to replace everything. How do we choose without being trapped?",
        "First question: does this technology solve a concrete problem we actually have? Not the problem Twitter says we have.",
        "Second question: what is the cost of dependence if the vendor disappears? Every technology has an exit cost. Calculate before entry.",
        "Third question: who will maintain this 24 months from now? If the answer is 'no one knows', step back to the more boring but proven choice.",
        "Our stack this year may look boring: PostgreSQL, FastAPI, React, Postgres+pgvector for AI. That is intentional. We choose technologies we can defend to clients five years from now.",
      ],
    },
  },
  {
    slug: "ux-research-untuk-pasar-indonesia",
    title: {
      id: "UX Research untuk Pasar Indonesia — Konteks yang Sering Terlewat",
      en: "UX Research for the Indonesian Market — Context That Often Gets Missed",
    },
    excerpt: {
      id: "Persona barat tidak menerjemahkan dengan baik di pasar Indonesia. Inilah cara kami menyesuaikan riset untuk konteks lokal yang sebenarnya.",
      en: "Western personas don't translate well to the Indonesian market. Here is how we adapt research for true local context.",
    },
    category: { id: "UX Research", en: "UX Research" },
    readingTime: 7,
    date: "2025-10-04",
    cover: "linear-gradient(135deg, #d8ff5c 0%, #5b4bff 200%)",
    body: {
      id: [
        "Kami pernah membangun produk yang sukses di benchmark global dan gagal total di Indonesia. Pelajaran: konteks lokal harus jadi penelitian primer, bukan asumsi.",
        "Wawancara di Jakarta sangat berbeda dari di Surabaya. Daya beli, ekspektasi UX, hingga kecepatan internet yang dapat diandalkan — semua berbeda. Persona harus mengakomodasi variasi ini.",
        "Mobile-first di Indonesia bukan tren — itu kenyataan struktural. 87% pengguna kami selama 3 tahun terakhir mengakses produk klien lewat ponsel di bawah Rp 4 juta.",
        "Bahasa adalah keputusan desain, bukan keputusan terjemahan. Frasa 'Anda', 'kamu', dan 'lo' membawa konteks emosional yang sangat berbeda — pilih dengan sadar.",
        "Riset etnografi singkat (4–6 jam mengamati pengguna di lingkungan asli mereka) sering lebih bernilai daripada 40 wawancara terjadwal.",
      ],
      en: [
        "We once built a product that performed well on global benchmarks and failed completely in Indonesia. The lesson: local context must be primary research, not assumption.",
        "Interviews in Jakarta differ sharply from those in Surabaya. Purchasing power, UX expectations, even reliable internet speed — all vary. Personas must reflect this variation.",
        "Mobile-first in Indonesia isn't a trend — it is a structural reality. 87% of users we've seen over 3 years access clients' products via phones priced under IDR 4M.",
        "Language is a design decision, not a translation decision. 'Anda', 'kamu', and 'lo' carry vastly different emotional contexts — choose deliberately.",
        "Short ethnographic research (4–6 hours of observing users in their natural environment) is often more valuable than 40 scheduled interviews.",
      ],
    },
  },
  {
    slug: "membangun-tim-engineering-jarak-jauh",
    title: {
      id: "Membangun Tim Engineering Jarak Jauh yang Sebenarnya Bekerja",
      en: "Building a Remote Engineering Team That Actually Ships",
    },
    excerpt: {
      id: "Lima ritual sederhana yang membuat tim lintas pulau kami tetap selaras tanpa rapat berlebihan atau micromanagement.",
      en: "Five simple rituals that keep our cross-island team aligned without excessive meetings or micromanagement.",
    },
    category: { id: "Operasional", en: "Operations" },
    readingTime: 5,
    date: "2025-09-18",
    body: {
      id: [
        "Tim kami tersebar dari Banda Aceh sampai Makassar. Tidak ada satu kantor pusat. Yang membuatnya berfungsi bukanlah tooling — melainkan disiplin proses.",
        "Ritual 1 — Daily async update tertulis maksimal lima baris. Tidak ada rapat harian. Yang penting tertulis, dapat dibaca ulang, dan tidak menyita zona waktu.",
        "Ritual 2 — Working hours yang dideklarasikan publik. Setiap engineer menulis jamnya di kalender bersama. Yang lain tahu kapan harus tidak mengganggu.",
        "Ritual 3 — Pair programming dua kali seminggu, lintas kota. Knowledge transfer terjadi secara organik, bukan lewat dokumen yang tak pernah dibaca.",
        "Ritual 4 — Definisi 'selesai' yang ketat. Kode tanpa tes, dokumen, dan deploy script bukan kode yang selesai. Kami menolak konsep 'sudah hampir selesai'.",
        "Ritual 5 — Retrospektif bulanan dengan satu pertanyaan: apa yang akan kita berhentikan? Menambah ritual mudah, menghentikan sulit. Sehat tetap.",
      ],
      en: [
        "Our team is spread from Banda Aceh to Makassar. There is no single HQ. What makes it work isn't tooling — it's process discipline.",
        "Ritual 1 — Daily written async updates, five lines max. No daily stand-up. What matters is written, re-readable, and doesn't burn time zones.",
        "Ritual 2 — Publicly declared working hours. Each engineer publishes their hours on a shared calendar. Everyone knows when not to disturb.",
        "Ritual 3 — Pair programming twice a week, across cities. Knowledge transfers organically, not via documents nobody reads.",
        "Ritual 4 — A strict definition of done. Code without tests, docs, and deploy scripts is not done code. We reject 'almost done'.",
        "Ritual 5 — Monthly retrospective with one question: what should we stop doing? Adding rituals is easy, stopping them is hard. Stay lean.",
      ],
    },
  },
  {
    slug: "menulis-rfp-yang-tidak-membuat-vendor-menebak",
    title: {
      id: "Menulis RFP yang Tidak Membuat Vendor Hanya Menebak",
      en: "Writing an RFP That Doesn't Force Vendors to Guess",
    },
    excerpt: {
      id: "Kebanyakan RFP teknologi gagal karena terlalu sibuk mendeskripsikan solusi, bukan masalahnya. Inilah cara membaliknya.",
      en: "Most technology RFPs fail because they describe the solution, not the problem. Here is how to flip that.",
    },
    category: { id: "Procurement", en: "Procurement" },
    readingTime: 6,
    date: "2025-08-22",
    body: {
      id: [
        "RFP yang baik dimulai dari masalah, bukan dari solusi. Tetapi 8 dari 10 RFP yang kami terima sudah menentukan stack, vendor, dan timeline sebelum konteks dipahami.",
        "Pertama, tulis konteks bisnis sebelum requirement teknis. Vendor terbaik akan menantang asumsi Anda — beri mereka ruang untuk melakukannya.",
        "Kedua, pisahkan must-have dari nice-to-have secara jujur. Daftar 87 fitur 'wajib' adalah cara paling cepat menjamin kegagalan delivery.",
        "Ketiga, sebutkan kriteria keberhasilan yang terukur — bukan deliverable. 'Sistem cepat' bukan kriteria; 'response time p95 di bawah 300ms' adalah kriteria.",
        "Keempat, alokasikan anggaran riset diskusi pra-proposal. Vendor yang serius butuh memahami konteks Anda sebelum berkomitmen pada harga.",
        "Kelima, evaluasi proses kerja, bukan presentasi. Vendor yang pandai presentasi belum tentu pandai delivery. Minta walkthrough proyek nyata mereka.",
      ],
      en: [
        "A good RFP starts from the problem, not the solution. Yet 8 out of 10 RFPs we receive have already chosen the stack, vendor, and timeline before context is understood.",
        "First, write the business context before technical requirements. The best vendors will challenge your assumptions — give them space to do so.",
        "Second, separate must-haves from nice-to-haves honestly. A list of 87 'mandatory' features is the fastest way to guarantee delivery failure.",
        "Third, list measurable success criteria — not deliverables. 'Fast system' isn't a criterion; 'p95 response time under 300ms' is.",
        "Fourth, allocate budget for pre-proposal discovery. Serious vendors need to understand your context before committing to a price.",
        "Fifth, evaluate work process, not presentations. A vendor great at presenting isn't always great at delivering. Ask for walkthroughs of real projects.",
      ],
    },
  },
  {
    slug: "keamanan-siber-untuk-tim-bukan-untuk-pdf",
    title: {
      id: "Keamanan Siber untuk Tim — Bukan untuk PDF di Folder Compliance",
      en: "Cybersecurity for Teams — Not for PDFs in a Compliance Folder",
    },
    excerpt: {
      id: "Kebanyakan dokumen keamanan tidak pernah dibaca tim. Inilah cara membuat security menjadi bagian rutin kerja, bukan beban tahunan.",
      en: "Most security documents are never read by teams. Here is how to make security a routine part of work, not an annual chore.",
    },
    category: { id: "Security", en: "Security" },
    readingTime: 7,
    date: "2025-07-30",
    body: {
      id: [
        "Audit ISO 27001 yang berhasil tetapi insiden tetap terjadi adalah pola yang sangat lazim. Penyebabnya bukan kebijakannya — melainkan jaraknya dari kerja harian.",
        "Pelajaran 1 — Aturan tertulis tanpa mekanisme penegakan otomatis adalah aspirasi, bukan kontrol. Gunakan policy-as-code di pipeline, bukan PDF.",
        "Pelajaran 2 — Latihan tabletop kuartalan lebih bernilai daripada 200 halaman dokumen kebijakan. Tim belajar dari simulasi, bukan dari teks.",
        "Pelajaran 3 — Threat modeling pada awal fitur, bukan setelah launch. Lima belas menit di awal menghemat berbulan-bulan respon insiden.",
        "Pelajaran 4 — Least privilege adalah default, bukan permintaan khusus. Bila akses sulit dimintakan, tim akan menemukan jalan pintas — dan jalan pintas itu adalah kerentanan.",
        "Pelajaran 5 — Postmortem tanpa menyalahkan adalah satu-satunya yang menghasilkan perbaikan jujur. Hukum orangnya dan tim akan menyembunyikan insiden berikutnya.",
      ],
      en: [
        "Passing an ISO 27001 audit while incidents still happen is a very common pattern. The cause isn't the policy — it's its distance from daily work.",
        "Lesson 1 — Written rules without automated enforcement are aspirations, not controls. Use policy-as-code in your pipeline, not PDFs.",
        "Lesson 2 — Quarterly tabletop exercises are worth more than 200 pages of policy. Teams learn from simulation, not from text.",
        "Lesson 3 — Threat model at the start of a feature, not after launch. Fifteen minutes upfront saves months of incident response.",
        "Lesson 4 — Least privilege is the default, not a special request. If access is hard to obtain, teams will find shortcuts — and those shortcuts are vulnerabilities.",
        "Lesson 5 — Blameless postmortems are the only ones that produce honest improvement. Punish people, and the team will hide the next incident.",
      ],
    },
  },
];
